import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicCard } from '../utils/cards';
import { downloadVCF } from '../utils/vcf';
import { trackCardEvent } from '../utils/leads';
import { supabase } from '../utils/supabase';
import CardDisplay from './CardDisplay';
import LeadForm from './LeadForm';
import Loading from './Loading';
import { BRAND, brandTitle } from '../config/brand';

/** /c/:slug — what a visitor sees after scanning the QR code. */
export default function PublicCard() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, card: null, theme: null, error: '' });
  const [saving, setSaving] = useState(false);
  const [asking, setAsking] = useState(false);
  const [askFirst, setAskFirst] = useState(false); // lead form on: card setting + the account's plan

  useEffect(() => {
    let active = true;
    supabase.rpc('card_lead_capture', { p_slug: slug })
      .then(({ data }) => active && setAskFirst(data === true));
    getPublicCard(slug)
      .then(({ card, theme }) => active && setState({ loading: false, card, theme, error: '' }))
      .catch((err) => {
        console.error('Error loading card:', err);
        if (active) setState({ loading: false, card: null, theme: null, error: 'We could not load this card. Please try again.' });
      });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (state.card) document.title = brandTitle(state.card.full_name);
  }, [state.card]);

  // Count one view per visitor session; the cardholder looking at their own card doesn't count
  useEffect(() => {
    const card = state.card;
    if (!card) return;
    const key = `tapzo:viewed:${card.slug}`;
    try { if (sessionStorage.getItem(key)) return; } catch { /* private mode */ }
    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user?.id && data.session.user.id === card.user_id) return;
      trackCardEvent(card.slug, 'view');
      try { sessionStorage.setItem(key, '1'); } catch { /* ignore */ }
    });
  }, [state.card]);

  const download = useCallback(async () => {
    if (!state.card) return;
    setSaving(true);
    try {
      await downloadVCF(state.card, state.theme);
    } finally {
      setSaving(false);
    }
  }, [state.card, state.theme]);

  if (state.loading) return <Loading label="Loading card..." />;

  if (!state.card) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-line p-8 text-center max-w-md">
          <img src={BRAND.logo.mark} alt="" className="h-11 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold text-ink mb-2">Card not found</h1>
          <p className="text-ink-muted mb-6">{state.error || 'This card does not exist or is no longer active.'}</p>
          <a href="/" className="inline-flex h-11 items-center px-5 rounded-xl bg-blue text-white font-semibold hover:bg-blue-dark">Go to {BRAND.name}</a>
        </div>
      </div>
    );
  }

  const handleSave = () => {
    if (askFirst) return setAsking(true);
    trackCardEvent(state.card.slug, 'save');
    download();
  };
  const skip = () => {
    trackCardEvent(state.card.slug, 'save');
    download();
    setAsking(false);
  };

  // Phones: the card fills the screen. Larger screens: a phone-sized card, centered.
  return (
    <div className="min-h-screen sm:bg-surface sm:py-10">
      <CardDisplay
        card={state.card}
        theme={state.theme}
        onSave={handleSave}
        saving={saving}
        className="min-h-screen sm:min-h-[780px] sm:max-w-[420px] sm:mx-auto sm:rounded-[28px] sm:overflow-hidden sm:shadow-[0_20px_60px_rgba(11,21,48,0.18)] [&>div]:min-h-screen sm:[&>div]:min-h-[780px]"
      />
      {asking && (
        <LeadForm card={state.card} theme={state.theme} onDownload={download} onSkip={skip} onClose={() => setAsking(false)} />
      )}
    </div>
  );
}
